import invariant from 'invariant';
import ahoy from 'ahoy.js';

import {
  EVENT_CATEGORIES,
  EVENT_ACTION_IDS,
  ANALYTICS_PROVIDERS,
  AHOY_URLS,
  SEGMENT_EVENTS,
  SEGMENT_PLATFORM
} from 'dpl/constants/analytics';
import { getUserTimeZone } from 'dpl/shared/utils/date';
import { FEATURES } from 'dpl/shared/constants/features';
import isFeatureEnabled from 'dpl/util/isFeatureEnabled';

const timezone = getUserTimeZone();

ahoy.configure({
  visitsUrl: AHOY_URLS.visits,
  eventsUrl: AHOY_URLS.events
});

export function sendGAEvent(eventAction, opts = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventAction, opts);
  }
}

export function sendPageview(opts) {
  if (typeof window.gtag === 'function') {
    window.gtag('config', window.__DPL_GA_TRACKING_ID, {
      ...window.__DPL_GA_OPTIONS,
      ...opts
    });
  }
  ahoy.track('pageview', opts);
}

export function sendAhoyEvent(eventAction, opts) {
  ahoy.track(eventAction, opts);
}


export function sendFBEvent(eventName) {
  if (typeof window.fbq === 'function') {
    window.fbq('track', eventName);
  }
}

export function sendBingEvent(eventAction, opts = {}) {
  if ('uetq' in window) {
    window.uetq.push('event', eventAction, opts);
  }
}

export function sendRudderstackEvent(event, properties = {}) {
  invariant(
    Object.values(SEGMENT_EVENTS).includes(event),
    'Event must exist in SEGMENT_EVENTS constant.'
  );

  if ('rudderanalytics' in window) {
    window.rudderanalytics.track(event, {
      ...properties,
      app_version: CONFIG.appVersion,
      platform: SEGMENT_PLATFORM,
      timezone
    });
  }
}

export function sendSegmentEvent(event, properties = {}) {
  invariant(
    Object.values(SEGMENT_EVENTS).includes(event),
    'Event must exist in SEGMENT_EVENTS constant.'
  );

  if ('analytics' in window) {
    window.analytics.track(event, {
      ...properties,
      app_version: CONFIG.appVersion,
      platform: SEGMENT_PLATFORM,
      timezone
    });
  }

  if (isFeatureEnabled(FEATURES.RUDDERSTACK_TRACKING)) {
    sendRudderstackEvent(event, properties);
  }
}

export function sendAhoyAndGAEvents(eventAction, opts = {}) {
  sendGAEvent(eventAction, opts);
  sendAhoyEvent(eventAction, opts);
}

export function trackEvent(
  eventCategory,
  eventAction,
  opts,
  providers = [ANALYTICS_PROVIDERS.GA, ANALYTICS_PROVIDERS.AHOY]
) {
  invariant(
    Object.values(EVENT_CATEGORIES).includes(eventCategory),
    'Event category must exist in EVENT_CATEGORIES constant.'
  );

  return fn =>
    function eventTrackingFn(e, ...args) {
      if (!eventAction) {
        eventAction = e && typeof e.type === 'string'
          ? e.type
          : 'click';
      }

      const finalOpts = {
        event_category: eventCategory,
        ...opts
      };

      if (providers.includes(ANALYTICS_PROVIDERS.GA)) {
        sendGAEvent(eventAction, finalOpts);
      }

      if (providers.includes(ANALYTICS_PROVIDERS.AHOY)) {
        sendAhoyEvent(eventAction, finalOpts);
      }

      if (providers.includes(ANALYTICS_PROVIDERS.BING)) {
        sendBingEvent(eventAction, finalOpts);
      }

      if (providers.includes(ANALYTICS_PROVIDERS.FB)) {
        sendFBEvent(eventCategory);
      }

      return fn.call(this, e, ...args);
    };
}

export function trackConversion(actionId) {
  invariant(
    Object.values(EVENT_ACTION_IDS).includes(actionId),
    'Event actionId must exist in EVENT_ACTION_IDS constant.'
  );

  return fn =>
    function conversionTrackingFn(...args) {
      sendGAEvent('conversion', { send_to: actionId });

      return fn.call(this, ...args);
    };
}

export function sendRudderstackIdentify({
  userId,
  traits
}) {
  if ('rudderanalytics' in window) {
    window.rudderanalytics.identify(userId, traits);
  }
}

// Segment
export function sendSegmentIdentify({
  userId,
  traits
}) {
  if ('analytics' in window) {
    window.analytics.identify(
      userId,
      traits
    );
  }

  if (isFeatureEnabled(FEATURES.RUDDERSTACK_TRACKING)) {
    sendRudderstackIdentify({ userId, traits });
  }
}

export function segmentReset() {
  if ('analytics' in window) {
    window.analytics.reset();
  }
}
